import { FormState } from '@app/lib/form/contracts/FormState';
import { useOwnerFormFields } from '../_sections/setups/useOwnerFormFields';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { useTranslations } from 'next-intl';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { Heading } from '@app/app/_components/Heading/Heading';
import { useCallback, useState, useRef } from 'react';
import { SideSheet } from '@app/app/_components/SideSheet/SideSheet';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { Button } from '@app/app/_components/Button/Button';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { CheckThinIcon } from '@app/app/_icons/CheckThinIcon';
import { twMerge } from 'tailwind-merge';
import { EditableVehicleOwner, EditableVehicleOwnerRef } from './EditableVehicleOwner';

interface Props {
    ownerFormFields: ReturnType<typeof useOwnerFormFields>;
    formState: FormState<PurchaseTransportInsuranceResult>;
    isCompany: boolean;
}

export function VehicleOwnerSummaryCard({ ownerFormFields, formState, isCompany }: Props) {
    const t = useTranslations();
    const [sideSheetOpen, setSideSheetOpen] = useState(false);
    const editableFormRef = useRef<EditableVehicleOwnerRef | null>(null);

    const handleOpenSideSheet = useCallback(() => {
        setSideSheetOpen(true);
    }, []);

    const handleCloseSideSheet = useCallback(() => {
        setSideSheetOpen(false);
    }, []);

    const handleCancel = useCallback(() => {
        handleCloseSideSheet();
    }, [handleCloseSideSheet]);

    const handleSave = useCallback(() => {
        if (editableFormRef.current) {
            const currentData = editableFormRef.current.getCurrentData();
            ownerFormFields.setOwnerFormData(currentData);
        }
        handleCloseSideSheet();
    }, [ownerFormFields, handleCloseSideSheet]);

    const sideSheetHeader = (
        <div className="flex items-center justify-between rounded-t-xl border-b border-neutral-300 px-6 py-4">
            <div className="flex items-center gap-3">
                <button className="cursor-pointer" onClick={handleCloseSideSheet}>
                    <ArrowLeftIcon className="h-5 w-5 text-neutral-500" />
                </button>
                <Heading level="h3">{t('eshop.transport.editVehicleOwnerInformation')}</Heading>
            </div>
        </div>
    );

    const sideSheetFooter = (
        <div className="flex h-min w-full items-center justify-between gap-6 bg-white px-6 py-4 shadow-2xl">
            <Button
                variant="outlined"
                className="w-full"
                rightAddon={<CloseIcon className="size-4" />}
                onClick={handleCancel}>
                {t('general.cancel')}
            </Button>
            <Button className="w-full" rightAddon={<CheckThinIcon className="size-5" />} onClick={handleSave}>
                {t('general.save')}
            </Button>
        </div>
    );

    return (
        <>
            <CollapsablePanel
                label={
                    isCompany
                        ? ownerFormFields.ownerFormData.ownerFirstName
                        : `${ownerFormFields.ownerFormData.ownerFirstName} ${ownerFormFields.ownerFormData.ownerLastName}`
                }
                collapsable={false}
                addon={<TextButton onClick={handleOpenSideSheet}>{t('general.edit')}</TextButton>}>
                <div className="px-6 py-2">
                    <div className="flex flex-col gap-2 border-b border-neutral-150 pb-2">
                        <Heading level="h4">
                            {isCompany ? t('profile.companyInformation') : t('eshop.transport.personalInformation')}
                        </Heading>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            <ReadOnlyField label={isCompany ? t('profile.companyNameLabel') : t('general.name')}>
                                {ownerFormFields.ownerFormData.ownerFirstName}
                            </ReadOnlyField>
                            {!isCompany && (
                                <ReadOnlyField label={t('general.surname')}>
                                    {ownerFormFields.ownerFormData.ownerLastName}
                                </ReadOnlyField>
                            )}
                            <ReadOnlyField
                                label={isCompany ? t('profile.companyCodeLabel') : t('general.personalCode')}>
                                {ownerFormFields.ownerFormData.ownerPersonalCode}
                            </ReadOnlyField>
                        </div>
                    </div>
                    <div
                        className={twMerge(
                            'flex flex-col gap-2 pt-2',
                            !ownerFormFields.ownerFormData.insuredByOwner && 'border-b border-neutral-150 py-2',
                        )}>
                        <Heading level="h4">{t('eshop.transport.contactInformation')}</Heading>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <ReadOnlyField
                                className="sm:col-span-2"
                                label={t('eshop.transport.streetNumberHouseAndApartment')}>
                                {`${ownerFormFields.ownerFormData.ownerAddress?.street} 
                            ${ownerFormFields.ownerFormData.ownerAddress?.houseNumber}${ownerFormFields.ownerFormData.ownerAddress?.apartmentNumber && `-${ownerFormFields.ownerFormData.ownerAddress?.apartmentNumber}`}, 
                            ${ownerFormFields.ownerFormData.ownerAddress?.city}, ${ownerFormFields.ownerFormData.ownerAddress?.postalCode}`}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.transport.phoneNumberLabel')}>
                                {ownerFormFields.ownerFormData.phoneNumber}
                            </ReadOnlyField>
                            <ReadOnlyField className="max-sm:break-words sm:text-nowrap" label={t('general.email')}>
                                {ownerFormFields.ownerFormData.email}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.transport.insurerOfTheVehicle')}>
                                {ownerFormFields.ownerFormData.insuredByOwner
                                    ? t('eshop.transport.insuredByOwnerHimself')
                                    : t('eshop.transport.insuredByOther')}
                            </ReadOnlyField>
                        </div>
                    </div>
                    {!ownerFormFields.ownerFormData.insuredByOwner && (
                        <div className="flex flex-col gap-2 pt-2">
                            <Heading level="h4">{t('eshop.transport.personalPayerInformation')}</Heading>
                            <div className="grid grid-cols-2 gap-2">
                                <ReadOnlyField label={t('general.name')}>
                                    {ownerFormFields.ownerFormData.payerFirstName}
                                </ReadOnlyField>
                                <ReadOnlyField label={t('general.surname')}>
                                    {ownerFormFields.ownerFormData.payerLastName}
                                </ReadOnlyField>
                                <ReadOnlyField label={t('general.personalCode')}>
                                    {ownerFormFields.ownerFormData.payerPersonalCode}
                                </ReadOnlyField>
                                <ReadOnlyField
                                    className="col-span-2"
                                    label={t('eshop.transport.streetNumberHouseAndApartment')}>
                                    {`${ownerFormFields.ownerFormData.payerAddress?.street} 
                            ${ownerFormFields.ownerFormData.payerAddress?.houseNumber}${ownerFormFields.ownerFormData.payerAddress?.apartmentNumber && `-${ownerFormFields.ownerFormData.payerAddress?.apartmentNumber}`}, 
                            ${ownerFormFields.ownerFormData.payerAddress?.city}, ${ownerFormFields.ownerFormData.payerAddress?.postalCode}`}
                                </ReadOnlyField>
                                <ReadOnlyField label={t('eshop.transport.phoneNumberLabel')}>
                                    {ownerFormFields.ownerFormData.payerPhoneNumber}
                                </ReadOnlyField>
                                <ReadOnlyField label={t('general.email')}>
                                    {ownerFormFields.ownerFormData.payerEmail}
                                </ReadOnlyField>
                            </div>
                        </div>
                    )}
                </div>
            </CollapsablePanel>

            <SideSheet open={sideSheetOpen} header={sideSheetHeader} footer={sideSheetFooter} contentClassName="p-6">
                {sideSheetOpen && (
                    <EditableVehicleOwner
                        key={`edit-form-${editableFormRef.current?.getCurrentData().insuredByOwner}`}
                        ref={editableFormRef}
                        initialData={ownerFormFields.ownerFormData}
                        formState={formState}
                        isCompany={isCompany}
                    />
                )}
            </SideSheet>
        </>
    );
}
